import React, { useState, useRef, useEffect, useMemo } from "react"
import Popup from "reactjs-popup"
import { useDispatch, useSelector } from "react-redux"
import { useAuth0 } from "@auth0/auth0-react"
import ConfirmationDialog from "../DailyCalendarFiles/CustomCalendar/ConfirmationDialog"
import {
	setIsEditing,
	transferPatientsBetweenNursesAndChairs
} from "../../features/schedule/scheduleSlice"
import { transferPatientsBetweenNursesAndChairsTomorrow } from "../../features/schedule/tomorrowSlice"
import { transferPatientsBetweenNursesAndChairsSandbox } from "../../features/schedule/sandboxSlice"
import { IoIosSwap } from "react-icons/io"
import { useSchedulePatch } from "../../hooks/useSchedulePatch"
import {
	getDateFormatted,
	getFirstOpenDateToday,
	getFirstOpenDateTomorrow,
	convertMinutesTo12HHMM
} from "../../utils/generalUtils"
import { startTimeAllCases } from "../../utils/realTimeUtils"
import SimpleFilterList from "./SimpleFilterList"
import useFetchSettings from "../../hooks/useFetchSettings"

const SwapPatients = ({
	selectedPatient,
	setNotification,
	isOpen,
	onClose,
	patientSchedule,
	nurseSchedule,
	closePopup,
	logging
}) => {
	const dispatch = useDispatch()
	const { getAccessTokenSilently } = useAuth0()
	const [isDialogOpen, setIsDialogOpen] = useState(false)
	const [targetPatient, setTargetPatient] = useState(null)
	const scrollableRef = useRef(null)
	const [isSaving, setIsSaving] = useState(false)
	const [searchTerm, setSearchTerm] = useState("")
	const [selectedAcuities, setSelectedAcuities] = useState([])
	const [swapType, setSwapType] = useState("nurse")
	const { settingsData } = useFetchSettings()
	const { user } = useAuth0()

	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
	const centerOpen = settingsData?.centerOpen
	const centerClosedDates = settingsData?.center_closed_dates || []
	const nextDate = getDateFormatted(
		getFirstOpenDateTomorrow(centerOpen, testEnvironment, centerClosedDates)
	)

	const sandbox = useSelector(state => state.sandbox.isSandboxMode)
	const isTomorrowMode = useSelector(state => state.tomorrow.isTomorrowMode)
	const schedulePatch = useSchedulePatch({ isTomorrowMode, sandbox })

	const date = isTomorrowMode
		? nextDate
		: getDateFormatted(getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates))

	// O(1) nurse lookup keyed by id — beats nurseSchedule.find(...) per row.
	const nurseById = useMemo(() => {
		const map = new Map()
		for (const nurse of nurseSchedule) map.set(nurse.nurseId, nurse)
		return map
	}, [nurseSchedule])

	// Pre-filter + sort the patient list once per relevant state change so
	// every keystroke in the search box doesn't iterate the full schedule
	// twice (once to filter, once to sort) plus an O(N) nurse find per row.
	const filteredPatients = useMemo(() => {
		if (!patientSchedule || !selectedPatient) return []
		const search = searchTerm.toLowerCase()
		return patientSchedule
			.filter(patient => {
				if (patient.patientId === selectedPatient.patientId) return false
				if (swapType === "nurse") {
					if (patient.assignedNurse === null) return false
					if (patient.assignedNurse === selectedPatient.assignedNurse) return false
				}
				if (swapType === "chair") {
					if (patient.assignedChair === null) return false
					if (patient.assignedChair === selectedPatient.assignedChair) return false
				}
				if (swapType === "both") {
					if (patient.assignedNurse === null || patient.assignedChair === null) {
						return false
					}
				}
				if (selectedAcuities.length > 0 && !selectedAcuities.includes(patient.acuity)) {
					return false
				}
				if (search) {
					const nurseName = nurseById.get(patient.assignedNurse)?.nurseName
					const label = `${patient.patientName} (${firstNameLastInitial(
						nurseName
					)}) (${convertMinutesTo12HHMM(
						startTimeAllCases(patient, sandbox, isTomorrowMode)
					)})`.toLowerCase()
					if (!label.includes(search)) return false
				}
				return true
			})
			.sort(
				(a, b) =>
					startTimeAllCases(a, sandbox, isTomorrowMode) -
					startTimeAllCases(b, sandbox, isTomorrowMode)
			)
	}, [
		patientSchedule,
		selectedPatient,
		swapType,
		searchTerm,
		selectedAcuities,
		nurseById,
		sandbox,
		isTomorrowMode
	])

	const handleOpen = () => {
		logging.info("SwapPatients - Popup Opened", {
			component: "SwapPatients",
			function: "handleOpen",
			selectedPatient: selectedPatient
		})
		// When the popup opens, scroll to top
		if (scrollableRef.current) {
			scrollableRef.current.scrollTop = 0
		}
	}

	const handleClose = () => {
		logging.info("SwapPatients - Popup Closed", {
			component: "SwapPatients",
			function: "handleClose",
			selectedPatient: selectedPatient
		})
		if (!isSaving) {
			dispatch(setIsEditing(false))
		}
		setSearchTerm("")
		setSelectedAcuities([])
		onClose()
	}

	const handleSwapPatient = patient => {
		logging.info("SwapPatients - Selecting patient to swap", {
			component: "SwapPatients",
			function: "handleSwapPatient",
			patientSwapping: selectedPatient,
			patientToSwapWith: patient
		})
		// Otherwise proceed with normal swap dialog
		setTargetPatient(patient)
		setIsDialogOpen(true)
	}

	const swapPatientAssignment = (selectedPatient, targetPatient) => {
		// Create reassignments array

		const reassignments = [
			// Move first patient to second patient's nurse/chair
			{
				patientId: selectedPatient.patientId,
				...(swapType === "chair" || swapType === "both"
					? { newChairId: targetPatient.assignedChair }
					: {}),
				...(swapType === "nurse" || swapType === "both"
					? { newNurseId: targetPatient.assignedNurse }
					: {})
			},
			// Move second patient to first patient's nurse/chair
			{
				patientId: targetPatient.patientId,
				...(swapType === "chair" || swapType === "both"
					? { newChairId: selectedPatient.assignedChair }
					: {}),
				...(swapType === "nurse" || swapType === "both"
					? { newNurseId: selectedPatient.assignedNurse }
					: {})
			}
		]

		// Dispatch appropriate action based on mode
		if (sandbox) {
			dispatch(transferPatientsBetweenNursesAndChairsSandbox(reassignments))
		} else if (isTomorrowMode) {
			dispatch(transferPatientsBetweenNursesAndChairsTomorrow(reassignments))
		} else {
			dispatch(transferPatientsBetweenNursesAndChairs(reassignments))
		}
	}

	const confirmSwap = async () => {
		logging.info("SwapPatients - Confirming swap", {
			component: "SwapPatients",
			function: "confirmSwap",
			selectedPatient: selectedPatient,
			targetPatient: targetPatient
		})
		setIsDialogOpen(false)
		setIsSaving(true)
		dispatch(setIsEditing(true))

		try {
			// Find the nurses in the updated schedule
			swapPatientAssignment(selectedPatient, targetPatient)

			closePopup()

			if (!sandbox) {
				try {
					await getAccessTokenSilently({
						audience: process.env.REACT_APP_API_IDENTIFIER
					})
					await schedulePatch.mutateAsync({ date, setNotification })
				} catch (error) {
					logging.error("Error saving schedule:", error)
				}
			}

			dispatch(setIsEditing(false))

			setNotification({
				visible: true,
				message: "Successfully swapped patient assignments!",
				success: true
			})

			logging.info("SwapPatients - Successfully swapped patient assignments", {
				component: "SwapPatients",
				function: "confirmSwap",
				patientSwapping: selectedPatient,
				patientToSwapWith: targetPatient
			})
			setTimeout(() => setNotification({ visible: false, message: "" }), 3000)
		} catch (error) {
			logging.error("Error swapping patient assignments:", error)
			setNotification({
				visible: true,
				message: "Error swapping patient assignments",
				success: false
			})
		} finally {
			setIsSaving(false) // Reset saving state

			setTimeout(() => setNotification({ visible: false, message: "" }), 3000)
		}
	}

	useEffect(() => {
		// Set scroll to top whenever the component is mounted
		if (scrollableRef.current) {
			scrollableRef.current.scrollTop = 0
		}
	}, []) // Empty dependency ensures it runs only on mount

	function escapeRegExp(string) {
		return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
	}

	function getSwapTypeString() {
		if (swapType === "nurse") {
			return "Nurses"
		} else if (swapType === "chair") {
			return "Chairs"
		} else if (swapType === "both") {
			return "Nurses and Chairs"
		}
	}

	function firstNameLastInitial(name) {
		if (!name) {
			return "Unassigned"
		}
		const [firstName, lastName] = name.split(" ")
		return `${firstName} ${lastName[0]}.`
	}

	return (
		<>
			<Popup
				open={isOpen}
				onOpen={handleOpen}
				onClose={handleClose}
				modal
				nested
				contentStyle={{ width: "auto", border: "none", borderRadius: "10px", padding: "0" }}
				overlayStyle={{ background: "rgba(0, 0, 0, 0.5)" }}
			>
				{close => (
					<div
						className="bg-white p-6 rounded-2xl shadow-lg w-96 mx-auto max-h-96 overflow-y-auto"
						ref={scrollableRef}
					>
						<div className="flex items-center text-xl font-semibold mb-6 text-[#091556]">
							<IoIosSwap className="w-5 h-5 mr-2" />
							Select Patient to Swap With
						</div>
						<div className="mb-4">
							{/* Search box for filtering patients */}

							<SimpleFilterList
								selectedAcuities={selectedAcuities}
								setSelectedAcuities={setSelectedAcuities}
								setSearchTerm={setSearchTerm}
							/>

							<div className="flex space-x-4 mb-4">
								<button
									className={`flex-1 py-2 px-4 rounded-md font-medium ${
										swapType === "nurse" || swapType === "both"
											? "bg-customBlue text-white"
											: "bg-gray-100 text-gray-700 hover:bg-gray-200"
									}`}
									onClick={() => {
										if (swapType === "nurse") {
											setSwapType("chair")
										} else if (swapType === "both") {
											setSwapType("chair")
										} else if (swapType === "chair") {
											setSwapType("both")
										} else {
											setSwapType("nurse")
										}
									}}
								>
									Swap Nurse
								</button>
								<button
									className={`flex-1 py-2 px-4 rounded-md font-medium ${
										swapType === "chair" || swapType === "both"
											? "bg-customBlue text-white"
											: "bg-gray-100 text-gray-700 hover:bg-gray-200"
									}`}
									onClick={() => {
										if (swapType === "chair") {
											setSwapType("nurse")
										} else if (swapType === "both") {
											setSwapType("nurse")
										} else if (swapType === "nurse") {
											setSwapType("both")
										} else {
											setSwapType("chair")
										}
									}}
								>
									Swap Chair
								</button>
							</div>

							{filteredPatients.map(patient => {
										const patientName = patient.patientName
										const nurseName = firstNameLastInitial(
											nurseById.get(patient.assignedNurse)?.nurseName
										)
										const startTime = convertMinutesTo12HHMM(
											startTimeAllCases(patient, sandbox, isTomorrowMode)
										)

										// Create the full display string with the patient name bolded
										const displayString = `<span class="font-bold">${patientName}</span> (${nurseName}) (${startTime})`

										const safeTerm = escapeRegExp(searchTerm)
										const highlightedString =
											searchTerm.length > 1
												? displayString.replace(
														new RegExp(`(${safeTerm})`, "gi"),
														match =>
															`<span class="sensitive bg-yellow-300">${match}</span>`
													)
												: displayString

										return (
											<div
												key={patient.patientId}
												className="cursor-pointer p-2 flex items-center hover:bg-[#F3F4F6] rounded-md"
												onClick={() => {
													handleSwapPatient(patient)
													close()
												}}
											>
												<div className="flex items-center justify-center w-7 h-7 bg-[#959DC5] text-white font-semibold rounded-full text-xs mr-3">
													{`${patientName
														.split(" ")
														.map(n => n[0])
														.join("")} `}
												</div>
												<span
													className="sensitive"
													dangerouslySetInnerHTML={{
														__html: highlightedString
													}}
												/>
											</div>
										)
									})}
						</div>
						<div className="flex justify-end">
							<button
								onClick={onClose}
								className="bg-gray-200 text-gray-700 text-sm font-semibold px-4 py-2 rounded-lg hover:bg-gray-300 transition"
							>
								Cancel
							</button>
						</div>
					</div>
				)}
			</Popup>

			<ConfirmationDialog
				title="Confirm Swap"
				message={`Are you sure you want to swap ${selectedPatient?.patientName} and ${targetPatient?.patientName}'s ${getSwapTypeString()}?`}
				onConfirm={confirmSwap}
				onCancel={() => {
					logging.info("SwapPatients - Cancelling swap", {
						component: "SwapPatients",
						function: "confirmSwap",
						selectedPatient: selectedPatient,
						targetPatient: targetPatient
					})
					setIsDialogOpen(false)
				}}
				isOpen={isDialogOpen}
				onClose={() => setIsDialogOpen(false)}
			/>
		</>
	)
}

export default SwapPatients
